import { SupabaseClient } from '@supabase/supabase-js';
import { InitialDevelopmentTreeState, LoadDocumentOptions, LoadedDocument, Persistor, PersistorErrorType } from './Persistor';
import { SkillData, SkillDataState, isValidSkillData, EntitlementLevel, UserTree, Result, ok, fail } from '@skill-trees/shared';

export class SupabasePersistor implements Persistor {
    constructor(
        private readonly supabase: SupabaseClient
    ) {}
    
    private async ensureAuthenticated() {
        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Authentication required for this operation');
        }
        return session;
    }

    private checkSupabaseResponse(result: any, errorPrefix: string): any {
        if (result.error) {
            const errorMessage = typeof result.error === 'string' ? result.error : result.error.message || 'Unknown error';
            throw new Error(`${errorPrefix}${errorMessage}`);
        }

        if (!result.data) {
            throw new Error(`${errorPrefix}No data returned`);
        }

        return result.data;
    }

    /**
     * Extracts the HTTP status code from a Supabase functions.invoke error.
     * Handles both SDK-style FunctionsHttpError (Response in context) and legacy error shapes.
     */
    private async extractErrorStatus(error: unknown): Promise<{ status: number; message?: string } | null> {
        if (error == null) return null;

        const maybeHttp = error as { name?: string; context?: unknown };
        if (maybeHttp.name === 'FunctionsHttpError' && maybeHttp.context instanceof Response) {
            const response = maybeHttp.context;
            let serverMessage: string | undefined;
            try {
                const body = await response.clone().json();
                if (body && typeof body.error === 'string') {
                    serverMessage = body.error;
                }
            } catch { /* response body may not be JSON */ }
            return { status: response.status, message: serverMessage };
        }

        const legacy = error as { status?: number; message?: string; context?: { body?: { error?: string } } };
        if (typeof legacy.status === 'number') {
            return { status: legacy.status, message: legacy.context?.body?.error };
        }

        return null;
    }


    async saveData(data: SkillData, treeId: string): Promise<void> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('save-tree', {
            body: { treeData: data, treeId }
        });

        this.checkSupabaseResponse({ data: result, error }, 'Failed to save skill tree data: ');
    }

    async saveDraft(data: SkillData, treeId: string): Promise<void> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('save-tree-draft', {
            body: { treeData: data, treeId }
        });

        this.checkSupabaseResponse({ data: result, error }, 'Failed to save skill tree draft: ');
    }

    async saveDocument(nodeId: string, content: string, treeId: string): Promise<void> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('save-document', {
            body: { nodeId, content, treeId }
        });

        this.checkSupabaseResponse({ data: result, error }, `Failed to save document for node ${nodeId}: `);
    }

    async publishGuide(nodeId: string, treeId: string): Promise<Result<void, PersistorErrorType>> {
        await this.ensureAuthenticated();

        const { data: result, error } = await this.supabase.functions.invoke('publish-guide', {
            method: 'POST',
            body: { treeId, nodeId },
        });

        if (error) {
            const errorInfo = await this.extractErrorStatus(error);
            if (errorInfo?.status === 403) {
                return fail(errorInfo.message?.trim() || 'You do not have permission to publish this guide', 'permission');
            }
            if (errorInfo?.status === 404) {
                return fail(errorInfo.message?.trim() || 'No draft guide to publish', 'not_found');
            }
            if (errorInfo?.status === 400) {
                return fail(errorInfo.message?.trim() || 'Invalid request', 'validation');
            }
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            return fail(`Failed to publish guide: ${errorMessage}`, 'system');
        }

        if (!result || !result.success) {
            return fail('Failed to publish guide: No success response received', 'system');
        }

        return ok(undefined);
    }

    async loadData(treeId: string): Promise<Result<SkillDataState, PersistorErrorType>> {
        const { data: result, error } = await this.supabase.functions.invoke('load-tree', {
            method: 'POST',
            body: { treeId }
        });

        const errorInfo = await this.extractErrorStatus(error);
        if (errorInfo?.status === 403) {
            return fail(errorInfo.message?.trim() || 'This tree is private', 'permission');
        }

        const data = this.checkSupabaseResponse({ data: result, error }, 'Failed to load skill tree data: ');
        
        if (!isValidSkillData(data.treeData)) {
            throw new Error('Failed to load skill tree data: Invalid data format received');
        }
        
        return ok({
            treeData: data.treeData,
            entitlementLevel: (data.entitlementLevel ?? 'none') as EntitlementLevel,
            canEdit: data.canEdit ?? false
        });
    }

    async loadDraft(treeId: string): Promise<Result<SkillDataState, PersistorErrorType>> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('load-tree-draft', {
            method: 'POST',
            body: { treeId }
        });

        const errorInfo = await this.extractErrorStatus(error);
        if (errorInfo?.status === 403) {
            return fail(errorInfo.message?.trim() || 'This tree is private', 'permission');
        }

        const data = this.checkSupabaseResponse({ data: result, error }, 'Failed to load skill tree draft: ');
        
        if (!isValidSkillData(data.treeData)) {
            throw new Error('Failed to load skill tree draft: Invalid data format received');
        }
        
        return ok({
            treeData: data.treeData,
            entitlementLevel: (data.entitlementLevel ?? 'none') as EntitlementLevel,
            canEdit: data.canEdit ?? false
        });
    }

    async loadInitialDevelopmentTree(treeId: string): Promise<Result<InitialDevelopmentTreeState, PersistorErrorType>> {
        const { data: result, error } = await this.supabase.functions.invoke('load-initial-development-tree', {
            method: 'POST',
            body: { treeId }
        });

        const errorInfo = await this.extractErrorStatus(error);
        if (errorInfo?.status === 403) {
            return fail(errorInfo.message?.trim() || 'This tree is private', 'permission');
        }
        if (errorInfo?.status === 404) {
            return fail(errorInfo.message?.trim() || 'Tree not found', 'not_found');
        }

        const data = this.checkSupabaseResponse({ data: result, error }, 'Failed to load skill tree data: ');

        if (!isValidSkillData(data.treeData)) {
            throw new Error('Failed to load skill tree data: Invalid data format received');
        }
        if (data.publishedTree != null && !isValidSkillData(data.publishedTree)) {
            throw new Error('Failed to load skill tree data: Invalid published tree format received');
        }

        return ok({
            treeData: data.treeData,
            entitlementLevel: (data.entitlementLevel ?? 'none') as EntitlementLevel,
            canEdit: data.canEdit ?? false,
            isDraft: data.isDraft === true,
            publishedTree: data.publishedTree ?? null
        });
    }

    async loadDocument(nodeId: string, treeId: string, options?: LoadDocumentOptions): Promise<Result<LoadedDocument, PersistorErrorType>> {
        const { data: result, error } = await this.supabase.functions.invoke('load-document', {
            method: 'POST',
            body: options?.preferDraft ? { nodeId, treeId, preferDraft: true } : { nodeId, treeId }
        });

        const errorInfo = await this.extractErrorStatus(error);
        if (errorInfo?.status === 403) {
            return fail(errorInfo.message?.trim() || 'This tree is private', 'permission');
        }

        const data = this.checkSupabaseResponse({ data: result, error }, `Failed to load document for node ${nodeId}: `);
        return ok({ content: data.content, isDraft: data.isDraft === true });
    }

    async createTree(treeId: string, treeName: string, isPrivate: boolean): Promise<Result<void, PersistorErrorType>> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('create-tree', {
            body: { treeId, treeName, isPrivate }
        });

        if (error) {
            if (error.status === 409 || (error.message && error.message.includes('already exists'))) {
                return fail('A tree with this ID already exists', 'conflict');
            }
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            throw new Error(`Failed to create tree: ${errorMessage}`);
        }

        if (result && result.conflict) {
            return fail('A tree with this ID already exists', 'conflict');
        }

        if (!result || !result.success) {
            throw new Error('Failed to create tree: No success response received');
        }

        return ok(undefined);
    }

    async fetchUserTrees(): Promise<UserTree[]> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('list-user-trees', {
            method: 'POST',
            body: {}
        });

        if (error) {
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            throw new Error(`Failed to fetch user trees: ${errorMessage}`);
        }

        if (!result || !result.success) {
            throw new Error('Failed to fetch user trees: No success response received');
        }

        if (!Array.isArray(result.trees)) {
            throw new Error('Failed to fetch user trees: Invalid response format');
        }

        return result.trees as UserTree[];
    }

    async renameTree(treeId: string, newName: string): Promise<Result<void, PersistorErrorType>> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('rename-tree', {
            body: { treeId, newName }
        });

        if (error) {
            if (error.status === 403) {
                const errorMessage = (error.context?.body?.error) || (error.message) || 'You do not have permission to rename this tree';
                return fail(errorMessage, 'permission');
            }
            if (error.status === 404) {
                return fail('Tree not found', 'not_found');
            }
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            throw new Error(`Failed to rename tree: ${errorMessage}`);
        }

        if (!result || !result.success) {
            throw new Error('Failed to rename tree: No success response received');
        }

        return ok(undefined);
    }

    async deleteTree(treeId: string): Promise<Result<void, PersistorErrorType>> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('delete-tree', {
            body: { treeId }
        });

        if (error) {
            if (error.status === 403) {
                const errorMessage = (error.context?.body?.error) || (error.message) || 'You do not have permission to delete this tree';
                return fail(errorMessage, 'permission');
            }
            if (error.status === 404) {
                return fail('Tree not found', 'not_found');
            }
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            throw new Error(`Failed to delete tree: ${errorMessage}`);
        }

        if (!result || !result.success) {
            throw new Error('Failed to delete tree: No success response received');
        }

        return ok(undefined);
    }

    async toggleTreeVisibility(treeId: string): Promise<Result<boolean, PersistorErrorType>> {
        await this.ensureAuthenticated();
        
        const { data: result, error } = await this.supabase.functions.invoke('toggle-tree-visibility', {
            body: { treeId }
        });

        if (error) {
            if (error.status === 403) {
                const errorMessage = (error.context?.body?.error) || (error.message) || 'You do not have permission to modify this tree';
                return fail(errorMessage, 'permission');
            }
            if (error.status === 404) {
                return fail('Tree not found', 'not_found');
            }
            const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
            throw new Error(`Failed to toggle tree visibility: ${errorMessage}`);
        }

        if (!result || !result.success) {
            throw new Error('Failed to toggle tree visibility: No success response received');
        }

        return ok(result.isPrivate === true);
    }

}
